import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import type { ImageSearchProps, UnsplashImage } from './UnsplashImageSearch.helpers'
import { downloadUnsplashImage, getRandomDefaultSearchQuery, searchUnsplashImages } from './UnsplashImageSearch.helpers'
import { useApi } from '../ApiProvider'
import useLazyLoadMore from '../../utils/hooks/useLazyLoadMore'

const UnsplashContext = createContext<UnsplashContextType | undefined>(undefined)

export function UnsplashImageSearchProvider({ children, onSelect, onClose }: UnsplashImageSearchProviderProps) {
  const [searchState, setSearchState] = useState<SearchState>(INITIAL_STATE)
  const api = useApi()

  const {
    searchTerm,
    activeCategory,
    searchResults,
    selectedImage,
    isLoading,
    isNewSearch,
    isDownloading,
    persistentRandomSearchQuery,
    currentPage,
  } = searchState

  const updateSearchState = useCallback((updates: Partial<SearchState>) => {
    setSearchState((prev) => ({ ...prev, ...updates }))
  }, [])

  // Resets to initial values
  const resetSearchState = useCallback(() => {
    setSearchState(INITIAL_STATE)
  }, [])

  // Loads the next page of images when "Load More" is clicked or lazy loading is triggered
  const loadMoreImages = useCallback(async () => {
    if (isLoading || currentPage >= MAX_PAGES) return

    try {
      updateSearchState({ isLoading: true })

      const userInitiatedSearchQuery = activeCategory || searchTerm
      const nextPage = currentPage + 1
      const searchParams: ImageSearchProps = {
        perPage: IMAGES_PER_PAGE,
        page: nextPage,
      }

      if (userInitiatedSearchQuery.trim()) {
        // User is searching with specific term or category
        searchParams.query = userInitiatedSearchQuery
      } else if (persistentRandomSearchQuery) {
        // If lazy loading, use the persistent random search query
        searchParams.defaultSearchQuery = persistentRandomSearchQuery
      } else {
        // On initial load, use default search
        searchParams.defaultSearch = true
      }

      const results = await searchUnsplashImages(api, searchParams)

      updateSearchState({
        // Append new results to existing ones
        searchResults: [...searchResults, ...results],
        currentPage: nextPage,
        isLoading: false,
      })
    } catch {
      updateSearchState({
        isLoading: false,
      })
    }
  }, [
    activeCategory,
    api,
    isLoading,
    persistentRandomSearchQuery,
    searchResults,
    searchTerm,
    updateSearchState,
    currentPage,
  ])

  // Load initial default images
  useEffect(() => {
    let isMounted = true

    const fetchDefaultImages = async () => {
      try {
        const randomDefaultSearchQuery = getRandomDefaultSearchQuery()
        updateSearchState({
          isLoading: true,
          persistentRandomSearchQuery: randomDefaultSearchQuery,
          currentPage: 0,
        })

        const results = await searchUnsplashImages(api, {
          defaultSearchQuery: randomDefaultSearchQuery,
          perPage: IMAGES_PER_PAGE,
        })

        if (isMounted) {
          updateSearchState({
            searchResults: results,
            isLoading: false,
            currentPage: 1,
          })
        }
      } catch {
        if (isMounted) {
          updateSearchState({ isLoading: false })
        }
      }
    }

    fetchDefaultImages()

    return () => {
      isMounted = false
    }
  }, [api, updateSearchState])

  const searchUnsplash = useCallback(
    async (query?: string, fromCategory = false) => {
      if (!query) return

      try {
        updateSearchState({
          isLoading: true,
          selectedImage: null,
          isNewSearch: true, // Flag used to reset scroll position
          persistentRandomSearchQuery: '',
          currentPage: 0,
          ...(!fromCategory && { activeCategory: null }), // If search is not from category, clear any active category
        })

        const results = await searchUnsplashImages(api, {
          query,
          perPage: IMAGES_PER_PAGE,
          page: 1,
        })

        updateSearchState({
          searchResults: results,
          isLoading: false,
          isNewSearch: false,
          currentPage: 1,
        })
      } catch {
        updateSearchState({ isLoading: false })
      }
    },
    [api, updateSearchState],
  )

  const closeAndReset = useCallback(() => {
    // close popover
    if (onClose) onClose()
    // then reset state after a 300ms delay ensuring popover is closed
    setTimeout(() => {
      resetSearchState()
    }, 300) // 300ms is the close animation duration of popover
  }, [resetSearchState, onClose])

  // Downloads the full-size image from Unsplash
  const handleApplySelection = useCallback(async () => {
    if (!selectedImage) return

    try {
      updateSearchState({ isDownloading: true })
      const file = await downloadUnsplashImage(api, selectedImage)

      onSelect(file)

      closeAndReset()
      updateSearchState({ isDownloading: false })
    } catch {
      updateSearchState({ isDownloading: false })
    }
  }, [selectedImage, updateSearchState, api, onSelect, closeAndReset])

  const handleCategoryClick = useCallback(
    (category: string) => {
      const newActiveCategory = category === activeCategory ? null : category
      updateSearchState({
        activeCategory: newActiveCategory,
        persistentRandomSearchQuery: '',
      })
      searchUnsplash(category, true)
    },
    [activeCategory, searchUnsplash, updateSearchState],
  )

  const setSearchTerm = useCallback(
    (term: string) => {
      updateSearchState({ searchTerm: term })
    },
    [updateSearchState],
  )

  const setSelectedImage = useCallback(
    (image: UnsplashImage | null) => {
      updateSearchState({ selectedImage: image })
    },
    [updateSearchState],
  )

  const getLazyLoadRef = useLazyLoadMore(IMAGES_PER_PAGE, searchResults.length, loadMoreImages, 4)

  const shouldShowLoadMoreButton = currentPage === 1

  const value = useMemo(
    () => ({
      searchTerm,
      activeCategory,
      searchResults,
      selectedImage,
      isLoading,
      isNewSearch,
      isDownloading,
      persistentRandomSearchQuery,
      currentPage,
      shouldShowLoadMoreButton,

      setSearchTerm,
      searchUnsplash,
      setSelectedImage,
      handleCategoryClick,
      loadMoreImages,
      closeAndReset,
      handleApplySelection,
      getLazyLoadRef,
    }),
    [
      searchTerm,
      activeCategory,
      searchResults,
      selectedImage,
      isLoading,
      isNewSearch,
      isDownloading,
      persistentRandomSearchQuery,
      currentPage,
      shouldShowLoadMoreButton,

      setSearchTerm,
      searchUnsplash,
      setSelectedImage,
      handleCategoryClick,
      loadMoreImages,
      closeAndReset,
      handleApplySelection,
      getLazyLoadRef,
    ],
  )

  return <UnsplashContext.Provider value={value}>{children}</UnsplashContext.Provider>
}

export function useUnsplash() {
  const context = useContext(UnsplashContext)
  if (context === undefined) {
    throw new Error('useUnsplash must be used within a UnsplashImageSearchProvider')
  }
  return context
}

export const IMAGES_PER_PAGE = 30
export const MAX_PAGES = 3

export const INITIAL_STATE: SearchState = {
  searchTerm: '',
  activeCategory: null,
  searchResults: [],
  selectedImage: null,
  isLoading: false,
  isNewSearch: false,
  isDownloading: false,
  persistentRandomSearchQuery: '',
  currentPage: 0,
}

type SearchState = {
  searchTerm: string
  activeCategory: string | null
  searchResults: UnsplashImage[]
  selectedImage: UnsplashImage | null
  isLoading: boolean
  isNewSearch: boolean
  isDownloading: boolean
  persistentRandomSearchQuery: string // Persist query for infinite scroll
  currentPage: number
}

type UnsplashContextType = {
  searchTerm: string
  activeCategory: string | null
  searchResults: UnsplashImage[]
  selectedImage: UnsplashImage | null
  isLoading: boolean
  isNewSearch: boolean
  isDownloading: boolean
  persistentRandomSearchQuery: string
  currentPage: number
  shouldShowLoadMoreButton: boolean

  setSearchTerm: (term: string) => void
  searchUnsplash: (term?: string, fromCategory?: boolean) => Promise<void>
  setSelectedImage: (image: UnsplashImage | null) => void
  handleCategoryClick: (category: string) => void
  loadMoreImages: () => Promise<void>
  closeAndReset: () => void
  handleApplySelection: () => Promise<void>
  getLazyLoadRef: (index: number) => ((node?: Element | null) => void) | null
}

type UnsplashImageSearchProviderProps = {
  readonly children: ReactNode
  readonly onSelect: (file: File) => void
  readonly onClose: () => void
}
